import { currentLanguage, type Language, t } from "@i18n";

/** Number and date formats per UI language: Hungarian decimal comma and "2024. 10. 02."; English decimal point and
 *  "02/10/2024" (en-GB: day first, like the rest of Europe). */
const LOCALES: Record<Language, string> = { en: "en-GB", hu: "hu-HU" };

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.Collator>();
function cached<F extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.Collator>(key: string, make: (locale: string) => F): F {
  const lang = currentLanguage();
  const full = `${lang}|${key}`;
  let f = cache.get(full) as F | undefined;
  if (!f) {
    f = make(LOCALES[lang]);
    cache.set(full, f);
  }
  return f;
}

/** How a date is typed in a date field: Hungarian 2026. 09. 29., English 29/09/2026 (as ``formatDate`` prints it). */
export const dateInputFormat = () => ({ hu: "YYYY. MM. DD.", en: "DD/MM/YYYY" })[currentLanguage()];

/** The decimal separator of the UI language ("," Hungarian, "." English): number inputs and copied values. */
export const decimalSeparator = () => (currentLanguage() === "hu" ? "," : ".");

/** A number in the UI language: Hungarian 1 214 / 8,05, English 1,214 / 8.05. */
export function formatNumber(value: number | null | undefined, digits?: number): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return cached(
    `n${digits ?? -1}`,
    (locale) =>
      new Intl.NumberFormat(
        locale,
        digits === undefined ? { maximumFractionDigits: 3 } : { minimumFractionDigits: digits, maximumFractionDigits: digits },
      ),
  ).format(value);
}

/** A number with every significant decimal (up to 6), as copied to a spreadsheet of the UI language. */
export const formatCopyNumber = (value: number) =>
  cached("copy", (locale) => new Intl.NumberFormat(locale, { maximumFractionDigits: 6 })).format(value);

/** A number as typed (no grouping, every digit kept) with the UI language's decimal separator: 8,05 / 8.05. */
export const plainNumber = (value: number) => String(value).replace(".", decimalSeparator());

const dateFormat = (key: string, options: Intl.DateTimeFormatOptions) => cached(key, (locale) => new Intl.DateTimeFormat(locale, options));
const time = () => dateFormat("time", { hour: "2-digit", minute: "2-digit" });
const timeSec = () => dateFormat("timeSec", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const day = () => dateFormat("day", { month: "2-digit", day: "2-digit" });
const full = () => dateFormat("full", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const dateOnly = () => dateFormat("date", { year: "numeric", month: "2-digit", day: "2-digit" });

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** 10:48 today, 09. 18. earlier this year, full date otherwise. */
export function shortTime(iso: string | null | undefined, seconds = false): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return (seconds ? timeSec : time)().format(d);
  if (d.getFullYear() === now.getFullYear()) return day().format(d);
  return dateOnly().format(d);
}

/** A calendar date (an ISO "2024-10-02", no time of day) as "2024. 10. 02." / "02/10/2024". */
export function formatDate(iso: string | null | undefined): string {
  return iso ? dateOnly().format(new Date(`${iso.slice(0, 10)}T12:00:00`)) : "—";
}

export function fullTime(iso: string | null | undefined): string {
  return iso ? full().format(new Date(iso)) : "—";
}

/** "5 minutes ago", "2 hours ago", "yesterday" (time.*), or the date. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return t("time.justNow");
  if (minutes < 60) return t("time.minutesAgo", { count: minutes });
  if (minutes < 24 * 60 && sameDay(d, new Date())) return t("time.hoursAgo", { count: Math.round(minutes / 60) });
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(d, yesterday)) return t("time.yesterday");
  return shortTime(iso);
}

/** Numbers are right-aligned in tables. */
export const isNumericKind = (kind: string) => kind === "number" || kind === "integer";

/** A cell value for display: numbers in the UI language, empty as an em dash. A whole number is written without
 *  grouping (plot numbers, years, ids: 3002, not 3,002); only a number with decimals (a weight) is grouped, the
 *  breeders' rule (2026-10-29). */
export function cellText(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : formatNumber(value);
  if (typeof value === "boolean") return value ? t("value.yes") : t("value.no");
  return String(value);
}

/** Text in the order of the UI language, numbers inside it by value ("A2" before "A10"), case ignored. */
export const textCollator = () => cached("collator", (locale) => new Intl.Collator(locale, { numeric: true, sensitivity: "base" }));

/** Accent- and case-insensitive text for searching ("siker" finds "Sikér"). */
export const fold = (s: string) =>
  s
    .toLocaleLowerCase("hu")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/** A NumberInput's value as a number, or null while it is empty. */
export const num = (v: number | string) => (typeof v === "number" ? v : null);

/** A day in the browser's time zone as an ISO date (``toISOString`` would give the UTC date). */
export const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Today's date in the browser's time zone, as an ISO date. */
export const localToday = (): string => isoDate(new Date());
